import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Header, BottomNav } from "../components/Header";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { BarChart3, ShoppingBag, TrendingUp, DollarSign, Clock, CheckCircle, Package, Tag, Check, X, XCircle, MessageSquare, Star, RefreshCw, Trash2, Ban, Gavel, Loader2 } from "lucide-react";
import { AdminAuctionCard } from "../components/AuctionCards";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import axios from "axios";
import { toast } from "sonner";

const ActiveOffersPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [offers, setOffers] = useState([]);
  const [purchaseIntents, setPurchaseIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("offers");
  const [counterDialogOpen, setCounterDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [counterAmount, setCounterAmount] = useState(0);
  const [processing, setProcessing] = useState(false);
  
  // Rating state
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [rating, setRating] = useState(5);
  const [ratingComment, setRatingComment] = useState("");
  
  // Republish dialog state
  const [republishDialogOpen, setRepublishDialogOpen] = useState(false);
  const [republishProductId, setRepublishProductId] = useState(null);
  const [republishProductTitle, setRepublishProductTitle] = useState("");
  
  // Mark as paid + cashback dialog state
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [paidIntent, setPaidIntent] = useState(null);
  const [cashbackType, setCashbackType] = useState("amount");
  const [cashbackValue, setCashbackValue] = useState("");
  
  // Clear data dialog state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  // Monthly revenue report state
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [reportMonth, setReportMonth] = useState(currentMonth);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [trendData, setTrendData] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchReport = async (month) => {
    if (!token) return;
    setReportLoading(true);
    try {
      const res = await axios.get(`${API}/admin/revenue-report`, {
        params: { month },
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setReportData(res.data);
    } catch (error) {
      toast.error("Error al cargar el reporte de ingresos");
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    if (token && activeTab === "reports") {
      fetchReport(reportMonth);
    }
  }, [token, activeTab, reportMonth]);

  const MONTH_LABELS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

  const fetchTrend = async () => {
    if (!token) return;
    setTrendLoading(true);
    try {
      const res = await axios.get(`${API}/admin/revenue-trend`, {
        params: { months: 6 },
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      const trend = (res.data.trend || []).map((t) => ({
        ...t,
        label: MONTH_LABELS[parseInt(t.month.split("-")[1], 10) - 1]
      }));
      setTrendData(trend);
    } catch (error) {
      toast.error("Error al cargar la gráfica de ingresos");
    } finally {
      setTrendLoading(false);
    }
  };

  useEffect(() => {
    if (token && activeTab === "reports" && trendData.length === 0) {
      fetchTrend();
    }
  }, [token, activeTab]);

  const fetchData = async () => {
    if (!token) return;
    try {
      const [activeOffersRes, offersRes, intentsRes] = await Promise.all([
        axios.get(`${API}/admin/active-offers`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }),
        axios.get(`${API}/admin/offers`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }),
        axios.get(`${API}/admin/purchase-intents`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        })
      ]);
      setData(activeOffersRes.data);
      setOffers(offersRes.data);
      setPurchaseIntents(intentsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      const errorMsg = error.response?.data?.detail || error.message;
      console.error("Error detail:", errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSale = async (productId, confirmed) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/products/${productId}/confirm-sale?confirmed=${confirmed}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success(confirmed ? "Venta confirmada" : "Venta cancelada, producto republicado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar");
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const handleAcceptOffer = async (offerId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/offers/${offerId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Oferta aceptada");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aceptar oferta");
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectOffer = async (offerId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/offers/${offerId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Oferta rechazada");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar oferta");
    } finally {
      setProcessing(false);
    }
  };

  const openCounterDialog = (offer) => {
    setSelectedOffer(offer);
    setCounterAmount(Math.floor((offer.amount + offer.product_price) / 2)); // Suggest middle ground
    setCounterDialogOpen(true);
  };

  const handleSendCounter = async () => {
    if (!selectedOffer) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/offers/${selectedOffer.offer_id}/counter`, 
        { amount: counterAmount },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success("Contraoferta enviada");
      setCounterDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar contraoferta");
    } finally {
      setProcessing(false);
    }
  };

  // Purchase intent handlers
  const handleConfirmPurchaseIntent = async (intentId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/purchase-intents/${intentId}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("¡Compra confirmada!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al confirmar compra");
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelPurchaseIntent = async (intentId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/purchase-intents/${intentId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Solicitud de compra cancelada");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cancelar");
    } finally {
      setProcessing(false);
    }
  };

  // Rating handlers
  const openRatingDialog = (intent) => {
    setSelectedBuyer({ id: intent.buyer_id, name: intent.buyer_name });
    setRating(5);
    setRatingComment("");
    setRatingDialogOpen(true);
  };

  const handleSubmitRating = async () => {
    if (!selectedBuyer) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/users/${selectedBuyer.id}/rate`, 
        { rating, comment: ratingComment },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success(`Calificación guardada para ${selectedBuyer.name}`);
      setRatingDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar calificación");
    } finally {
      setProcessing(false);
    }
  };

  // Mark as paid (final confirmation) - opens cashback dialog first
  const openMarkAsPaid = (intent) => {
    setPaidIntent(intent);
    setCashbackType("amount");
    setCashbackValue("");
    setPaidDialogOpen(true);
  };

  const getComputedCashback = () => {
    const val = parseFloat(cashbackValue);
    if (isNaN(val) || val <= 0) return 2;
    if (cashbackType === "percent") return Math.round((paidIntent?.product_price || 0) * val) / 100;
    return Math.round(val * 100) / 100;
  };

  const handleMarkAsPaid = async () => {
    if (!paidIntent) return;
    setProcessing(true);
    try {
      const body = {};
      const val = parseFloat(cashbackValue);
      if (!isNaN(val) && val > 0) {
        body.cashback_type = cashbackType;
        body.cashback_value = val;
      }
      await axios.post(`${API}/purchase-intents/${paidIntent.intent_id}/mark-paid`, body, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success(`¡Marcado como pagado! Cashback para el comprador: $${getComputedCashback()}`);
      setPaidDialogOpen(false);
      setPaidIntent(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al marcar como pagado");
    } finally {
      setProcessing(false);
    }
  };

  // Delete product completely
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("¿Estás seguro de eliminar este producto permanentemente?")) return;
    setProcessing(true);
    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Producto eliminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar producto");
    } finally {
      setProcessing(false);
    }
  };

  // Revoke reservation (buyer didn't pay)
  const handleRevokeReservation = async (intentId) => {
    if (!window.confirm("¿Revocar la reserva? El comprador será notificado y podrás calificarlo.")) return;
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/purchase-intents/${intentId}/revoke`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Reserva revocada. Producto disponible nuevamente.");
      // Get intent info for rating and republish options
      const intent = purchaseIntents.find(i => i.intent_id === intentId);
      if (intent) {
        setSelectedBuyer({ id: intent.buyer_id, name: intent.buyer_name, revoked: true });
        setRating(1);
        setRatingComment("No completó la compra.");
        setRatingDialogOpen(true);
        
        // Ask if admin wants to republish the product
        setTimeout(() => {
          if (intent.source === "auction" && window.confirm(`¿Deseas republicar "${intent.product_title}" como una nueva subasta?`)) {
            openRepublishDialog(intent.product_id, intent.product_title);
          }
        }, 500);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al revocar reserva");
    } finally {
      setProcessing(false);
    }
  };

  // Alias for handleRevokeReservation - used in offer and auction intent sections
  const handleRevokeIntent = handleRevokeReservation;

  // Open republish dialog
  const openRepublishDialog = (productId, productTitle) => {
    setRepublishProductId(productId);
    setRepublishProductTitle(productTitle);
    setRepublishDialogOpen(true);
  };

  // Republish product with editing (navigate to create with data)
  const handleRepublishProduct = async (editFirst = false) => {
    if (!republishProductId) return;
    const productId = republishProductId;
    
    if (editFirst) {
      // Get full product data and navigate to create page
      try {
        const response = await axios.get(`${API}/products/${productId}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        const product = response.data;
        
        // First republish to clear history
        await axios.post(`${API}/products/${productId}/republish`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        
        setRepublishDialogOpen(false);
        // Then navigate to create with product data
        navigate('/create', { state: { republishProduct: product } });
      } catch (error) {
        toast.error(error.response?.data?.detail || "Error al obtener datos del producto");
      }
    } else {
      // Just republish directly
      setProcessing(true);
      try {
        await axios.post(`${API}/products/${productId}/republish`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        toast.success("Producto republicado exitosamente");
        setRepublishDialogOpen(false);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Error al republicar producto");
      } finally {
        setProcessing(false);
      }
    }
  };

  // Clear all data function
  const handleClearAllData = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/admin/clear-all-data`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success(`¡Datos eliminados! Ofertas: ${response.data.deleted.offers}, Subastas: ${response.data.deleted.bids}, Notificaciones: ${response.data.deleted.notifications}`);
      setClearDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al limpiar datos");
    } finally {
      setProcessing(false);
    }
  };

  const pendingOffers = offers.filter(o => o.status === "pending");
  const counterOffers = offers.filter(o => o.status === "counter");
  const acceptedOffers = offers.filter(o => o.status === "accepted");
  const processedOffers = offers.filter(o => !["pending", "counter", "accepted"].includes(o.status));
  const pendingIntents = purchaseIntents.filter(i => i.status === "pending");
  const acceptedIntents = purchaseIntents.filter(i => i.status === "accepted");
  const completedIntents = purchaseIntents.filter(i => i.status === "completed");
  const paidIntents = purchaseIntents.filter(i => i.status === "paid");
  const revokedIntents = purchaseIntents.filter(i => i.status === "revoked");
  
  // Filter intents by source for offers
  const offerIntentsPending = purchaseIntents.filter(i => i.source === "offer" || i.source === "counter_offer");
  const offerIntentsReserved = offerIntentsPending.filter(i => i.status === "accepted");
  const offerIntentsPaid = offerIntentsPending.filter(i => i.status === "paid");
  
  // Filter intents for Comprar Ahora (direct purchases: no source, or auction_buy_now = "Me interesa" on an auction listing)
  const directPendingIntents = purchaseIntents.filter(i => (!i.source || i.source === "auction_buy_now") && i.status === "pending");
  const directAcceptedIntents = purchaseIntents.filter(i => (!i.source || i.source === "auction_buy_now") && i.status === "accepted");
  const directPaidIntents = purchaseIntents.filter(i => (!i.source || i.source === "auction_buy_now") && i.status === "paid");
  
  // Filter intents for auctions
  const auctionIntentsReserved = purchaseIntents.filter(i => i.source === "auction" && i.status === "accepted");
  const auctionIntentsPaid = purchaseIntents.filter(i => i.source === "auction" && i.status === "paid");
  
  // Total intents that need attention (pending + accepted/reserved)
  const activeIntentsCount = pendingIntents.length + acceptedIntents.length;

  // Group bids by product for collapsible display
  const groupedBids = (data?.active_bids || []).reduce((acc, bid) => {
    const pid = bid.product_id;
    if (!acc[pid]) {
      acc[pid] = {
        productId: pid,
        productTitle: bid.product_title,
        productImage: bid.product_image,
        endTime: bid.product_end_time,
        bids: []
      };
    }
    acc[pid].bids.push(bid);
    return acc;
  }, {});
  
  const groupedBidsArray = Object.values(groupedBids);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="spinner"></div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="active-offers-page">
      <Header />
      
      <main className="max-w-5xl mx-auto px-4 py-6 pb-24 md:pb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-[#475569]" />
            <div>
              <h1 className="text-2xl font-bold text-[#0F172A]">Ofertas Activas</h1>
              <p className="text-sm text-[#64748B]">Ofertas, subastas y compras de tus productos</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setClearDialogOpen(true)}
            className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
            data-testid="clear-all-data-btn"
          >
            <Trash2 size={16} className="mr-2" />
            Limpiar Todo
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white border-[#E2E8F0]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-[#64748B]" />
                <span className="text-sm text-[#64748B]">Total Productos</span>
              </div>
              <p className="text-2xl font-bold text-[#0F172A] mt-1">{data?.total_products || 0}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-[#E2E8F0]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-sm text-[#64748B]">Vendidos</span>
              </div>
              <p className="text-2xl font-bold text-green-600 mt-1">{data?.total_sold || 0}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-[#E2E8F0]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-orange-500" />
                <span className="text-sm text-[#64748B]">Ofertas Pendientes</span>
              </div>
              <p className="text-2xl font-bold text-orange-600 mt-1">{pendingOffers.length}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-[#E2E8F0]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-500" />
                <span className="text-sm text-[#64748B]">Ingresos</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600 mt-1">${data?.total_revenue?.toFixed(2) || "0.00"}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 mb-4">
            <TabsTrigger value="offers" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-offers">
              <Tag size={14} />
              <span className="hidden sm:inline">Ofertas</span> ({pendingOffers.length + counterOffers.length})
            </TabsTrigger>
            <TabsTrigger value="intents" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-intents">
              <ShoppingBag size={14} />
              <span className="hidden sm:inline">Compras</span> ({directPendingIntents.length}/{directAcceptedIntents.length})
            </TabsTrigger>
            <TabsTrigger value="bids" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-bids">
              <Gavel size={14} />
              <span className="hidden sm:inline">Subastas</span> ({data?.active_bids?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="purchases" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-purchases">
              <CheckCircle size={14} />
              <span className="hidden sm:inline">Ventas</span> ({data?.purchases?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-reports">
              <BarChart3 size={14} />
              <span className="hidden sm:inline">Reportes</span>
            </TabsTrigger>
          </TabsList>

          {/* Purchase Intents Tab (Comprar Ahora - Direct purchases only) */}
          <TabsContent value="intents">
            {/* Pending intents - Solicitado */}
            {directPendingIntents.length === 0 && directAcceptedIntents.length === 0 && directPaidIntents.length === 0 ? (
              <Card className="bg-white border-[#E2E8F0]">
                <CardContent className="py-12 text-center">
                  <ShoppingBag className="h-12 w-12 text-[#CBD5E1] mx-auto mb-3" />
                  <p className="text-[#64748B]">No hay solicitudes de compra</p>
                  <p className="text-xs text-[#94A3B8] mt-1">Las solicitudes de "Comprar Ahora" aparecerán aquí</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Pending - Solicitado (Amarillo) */}
                {directPendingIntents.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-[#F59E0B] mb-2 flex items-center gap-2">
                      <Clock size={16} /> Solicitados ({directPendingIntents.length})
                    </h3>
                    <div className="space-y-3">
                      {directPendingIntents.map((intent) => (
                        <Card key={intent.intent_id} className="bg-[#FFFBEB] border-[#F59E0B]">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              <Link to={`/product/${intent.product_id}`}>
                                <img src={intent.product_image} alt={intent.product_title} className="w-16 h-16 object-cover rounded-lg" />
                              </Link>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-[#F59E0B] text-white text-xs">Solicitado</Badge>
                                </div>
                                <Link to={`/product/${intent.product_id}`} className="hover:text-[#475569]">
                                  <h3 className="font-semibold text-[#0F172A] truncate">{intent.product_title}</h3>
                                </Link>
                                <p className="text-sm text-[#64748B]">{intent.buyer_name}</p>
                                <p className="text-lg font-bold text-[#10B981]">${intent.product_price?.toLocaleString()}</p>
                              </div>
                              <div className="flex flex-col gap-2">
                                <Button size="sm" onClick={() => handleConfirmPurchaseIntent(intent.intent_id)} disabled={processing} className="bg-[#10B981] hover:bg-[#059669] rounded-full" data-testid={`accept-intent-${intent.intent_id}`}>
                                  <Check size={14} className="mr-1" /> Aceptar
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleCancelPurchaseIntent(intent.intent_id)} disabled={processing} className="border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2] rounded-full" data-testid={`reject-intent-${intent.intent_id}`}>
                                  <X size={14} className="mr-1" /> Rechazar
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Accepted - Reservado (Verde) */}
                {directAcceptedIntents.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-[#10B981] mb-2 flex items-center gap-2">
                      <CheckCircle size={16} /> Reservados ({directAcceptedIntents.length})
                    </h3>
                    <div className="space-y-3">
                      {directAcceptedIntents.map((intent) => (
                        <Card key={intent.intent_id} className="bg-[#F0FDF4] border-[#10B981]">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              <Link to={`/product/${intent.product_id}`}>
                                <img src={intent.product_image} alt={intent.product_title} className="w-16 h-16 object-cover rounded-lg" />
                              </Link>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-[#10B981] text-white text-xs">Reservado para {intent.buyer_name}</Badge>
                                </div>
                                <Link to={`/product/${intent.product_id}`} className="hover:text-[#475569]">
                                  <h3 className="font-semibold text-[#0F172A] truncate">{intent.product_title}</h3>
                                </Link>
                                <p className="text-lg font-bold text-[#10B981]">${intent.product_price?.toLocaleString()}</p>
                                <p className="text-xs text-[#F59E0B] flex items-center gap-1">
                                  <Clock size={12} />
                                  Expira en {Math.max(0, 3 - Math.floor((Date.now() - new Date(intent.created_at).getTime()) / (1000 * 60 * 60 * 24)))} días si no se paga
                                </p>
                              </div>
                              <div className="flex flex-col gap-2">
                                <Button size="sm" onClick={() => openMarkAsPaid(intent)} disabled={processing} className="bg-[#3B82F6] hover:bg-[#2563EB] rounded-full" data-testid={`mark-paid-${intent.intent_id}`}>
                                  <DollarSign size={14} className="mr-1" /> Pagado
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleRevokeReservation(intent.intent_id)} disabled={processing} className="border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2] rounded-full" data-testid={`revoke-${intent.intent_id}`}>
                                  <Ban size={14} className="mr-1" /> Revocar
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Paid - Pagado (Azul) */}
                {directPaidIntents.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-[#3B82F6] mb-2 flex items-center gap-2">
                      <DollarSign size={16} /> Pagados ({directPaidIntents.length})
                    </h3>
                    <div className="space-y-3">
                      {directPaidIntents.map((intent) => (
                        <Card key={intent.intent_id} className="bg-[#EFF6FF] border-[#3B82F6]">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              <img src={intent.product_image} alt={intent.product_title} className="w-16 h-16 object-cover rounded-lg" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-[#3B82F6] text-white text-xs">Pagado</Badge>
                                </div>
                                <h3 className="font-semibold text-[#0F172A] truncate">{intent.product_title}</h3>
                                <p className="text-sm text-[#64748B]">{intent.buyer_name}</p>
                                <p className="text-lg font-bold text-[#10B981]">${intent.product_price?.toLocaleString()}</p>
                              </div>
                              <div className="flex flex-col gap-2">
                                <Button size="sm" variant="outline" onClick={() => openRatingDialog(intent)} className="rounded-full" data-testid={`rate-buyer-${intent.intent_id}`}>
                                  <Star size={14} className="mr-1 text-[#F59E0B]" /> Calificar
                                </Button>
                                <Button size="sm" onClick={() => openRepublishDialog(intent.product_id, intent.product_title)} disabled={processing} className="bg-[#10B981] hover:bg-[#059669] rounded-full" data-testid={`republish-${intent.intent_id}`}>
                                  <RefreshCw size={14} className="mr-1" /> Republicar
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleDeleteProduct(intent.product_id)} disabled={processing} className="border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2] rounded-full" data-testid={`delete-${intent.intent_id}`}>
                                  <Trash2 size={14} className="mr-1" /> Borrar
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers">
            {pendingOffers.length === 0 && counterOffers.length === 0 ? (
              <Card className="bg-white border-[#E2E8F0]">
                <CardContent className="py-12 text-center">
                  <Tag className="h-12 w-12 text-[#CBD5E1] mx-auto mb-3" />
                  <p className="text-[#64748B]">No hay ofertas pendientes</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Pending Offers - esperando respuesta del admin */}
                {pendingOffers.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-[#F59E0B] mb-2 flex items-center gap-2">
                      <Clock size={16} /> Nuevas Ofertas ({pendingOffers.length})
                    </h3>
                    <div className="space-y-3">
                      {pendingOffers.map((offer) => (
                  <Card key={offer.offer_id} className="bg-white border-[#E2E8F0] hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <Link to={`/product/${offer.product_id}`}>
                          <img
                            src={offer.product_image}
                            alt={offer.product_title}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        </Link>
                        
                        <div className="flex-1 min-w-0">
                          <Link to={`/product/${offer.product_id}`} className="hover:text-[#475569]">
                            <h3 className="font-semibold text-[#0F172A] truncate">{offer.product_title}</h3>
                          </Link>
                          
                          <div className="flex items-center gap-2 mt-1">
                            <Avatar className="h-5 w-5">
                              <AvatarImage src={offer.buyer_picture} />
                              <AvatarFallback className="text-xs bg-[#475569] text-white">
                                {offer.buyer_name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm text-[#64748B]">{offer.buyer_name}</span>
                            {(offer.auto_accepted || offer.auto_countered) && (
                              <span
                                className="text-[9px] bg-amber-400 text-amber-900 font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide"
                                title={offer.auto_accepted ? "Aceptada automáticamente (superó tu precio mínimo)" : "Contraofertada automáticamente al precio mínimo"}
                                data-testid={`auto-badge-${offer.offer_id}`}
                              >
                                Auto
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-[#64748B]">Precio: <strong>${offer.product_price}</strong></span>
                            <span className="text-orange-600">Oferta: <strong>${offer.amount}</strong></span>
                            <span className="text-[#94A3B8]">
                              ({Math.round((1 - offer.amount / offer.product_price) * 100)}% menos)
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleAcceptOffer(offer.offer_id)}
                            disabled={processing}
                            className="bg-green-500 hover:bg-green-600 text-white"
                            data-testid={`accept-offer-${offer.offer_id}`}
                          >
                            <Check size={16} className="mr-1" />
                            Aceptar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openCounterDialog(offer)}
                            disabled={processing}
                            className="border-orange-500 text-orange-500 hover:bg-orange-50"
                            data-testid={`counter-offer-${offer.offer_id}`}
                          >
                            <MessageSquare size={16} className="mr-1" />
                            Contraoferta
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRejectOffer(offer.offer_id)}
                            disabled={processing}
                            className="border-red-500 text-red-500 hover:bg-red-50"
                            data-testid={`reject-offer-${offer.offer_id}`}
                          >
                            <X size={16} className="mr-1" />
                            Rechazar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                    </div>
                  </div>
                )}

                {/* Counter Offers Sent - esperando respuesta del comprador */}
                {counterOffers.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold text-orange-600 mb-2 flex items-center gap-2">
                      <MessageSquare size={16} /> Contraoferta Enviada ({counterOffers.length})
                    </h3>
                    <div className="space-y-3">
                      {counterOffers.map((offer) => (
                        <Card key={offer.offer_id} className="bg-orange-50 border-orange-300">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              <Link to={`/product/${offer.product_id}`}>
                                <img
                                  src={offer.product_image}
                                  alt={offer.product_title}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                              </Link>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-orange-500 text-white text-xs">Esperando respuesta</Badge>
                                </div>
                                <Link to={`/product/${offer.product_id}`} className="hover:text-[#475569]">
                                  <h3 className="font-semibold text-[#0F172A] truncate">{offer.product_title}</h3>
                                </Link>
                                <div className="flex items-center gap-2 mt-1">
                                  <Avatar className="h-5 w-5">
                                    <AvatarImage src={offer.buyer_picture} />
                                    <AvatarFallback className="text-xs bg-[#475569] text-white">
                                      {offer.buyer_name?.charAt(0)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm text-[#64748B]">{offer.buyer_name}</span>
                                  {(offer.auto_accepted || offer.auto_countered) && (
                                    <span
                                      className="text-[9px] bg-amber-400 text-amber-900 font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide"
                                      title={offer.auto_accepted ? "Aceptada automáticamente" : "Contraofertada automáticamente al precio mínimo"}
                                      data-testid={`auto-badge-counter-${offer.offer_id}`}
                                    >
                                      Auto
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 mt-2 text-sm">
                                  <span className="text-[#64748B]">Precio: <strong>${offer.product_price}</strong></span>
                                  <span className="text-[#94A3B8] line-through">Oferta: ${offer.amount}</span>
                                  <span className="text-orange-600 font-semibold">Tu contraoferta: ${offer.counter_amount}</span>
                                </div>
                              </div>
                              <div className="text-center text-sm text-orange-600">
                                <Clock size={20} className="mx-auto mb-1" />
                                Esperando...
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Offer-based Reserved Intents - Pendientes de pago */}
            {offerIntentsReserved.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-[#10B981] mb-2 flex items-center gap-2">
                  <CheckCircle size={16} /> Ofertas Aceptadas - Reservados ({offerIntentsReserved.length})
                </h3>
                <div className="space-y-3">
                  {offerIntentsReserved.map((intent) => (
                    <Card key={intent.intent_id} className="bg-[#D1FAE5] border-[#10B981]">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <Link to={`/product/${intent.product_id}`}>
                            <img src={intent.product_image} alt={intent.product_title} className="w-16 h-16 object-cover rounded-lg" />
                          </Link>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-[#10B981] text-white text-xs">Reservado para {intent.buyer_name}</Badge>
                            </div>
                            <Link to={`/product/${intent.product_id}`} className="hover:text-[#475569]">
                              <h3 className="font-semibold text-[#0F172A] truncate">{intent.product_title}</h3>
                            </Link>
                            <p className="text-lg font-bold text-[#10B981]">${intent.product_price?.toLocaleString()}</p>
                            <p className="text-xs text-[#F59E0B] flex items-center gap-1">
                              <Clock size={12} />
                              Expira en {Math.max(0, 3 - Math.floor((Date.now() - new Date(intent.created_at).getTime()) / (1000 * 60 * 60 * 24)))} días
                            </p>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button size="sm" onClick={() => openMarkAsPaid(intent)} disabled={processing} className="bg-[#3B82F6] hover:bg-[#2563EB] rounded-full" data-testid={`mark-paid-offer-${intent.intent_id}`}>
                              <DollarSign size={14} className="mr-1" /> Marcar Pagado
                            </Button>
                            {intent.buyer_phone && (
                              <a
                                href={`https://wa.me/${intent.buyer_phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hola ${intent.buyer_name}, tu compra de "${intent.product_title}" por $${intent.product_price} está reservada. Coordinemos la entrega.`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button size="sm" variant="outline" className="w-full border-[#34D399] text-[#34D399] hover:bg-[#D1FAE5] rounded-full">
                                  <MessageSquare size={14} className="mr-1" /> WhatsApp
                                </Button>
                              </a>
                            )}
                            <Button size="sm" variant="outline" onClick={() => handleRevokeIntent(intent.intent_id)} disabled={processing} className="border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2] rounded-full" data-testid={`revoke-offer-${intent.intent_id}`}>
                              <XCircle size={14} className="mr-1" /> Revocar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Offer-based Paid Intents - Pagados */}
            {offerIntentsPaid.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-[#3B82F6] mb-2 flex items-center gap-2">
                  <DollarSign size={16} /> Ofertas Pagadas ({offerIntentsPaid.length})
                </h3>
                <div className="space-y-3">
                  {offerIntentsPaid.map((intent) => (
                    <Card key={intent.intent_id} className="bg-[#EFF6FF] border-[#3B82F6]">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <img src={intent.product_image} alt={intent.product_title} className="w-16 h-16 object-cover rounded-lg" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-[#3B82F6] text-white text-xs">Pagado (Oferta)</Badge>
                            </div>
                            <h3 className="font-semibold text-[#0F172A] truncate">{intent.product_title}</h3>
                            <p className="text-sm text-[#64748B]">{intent.buyer_name}</p>
                            <p className="text-lg font-bold text-[#10B981]">${intent.product_price?.toLocaleString()}</p>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button size="sm" variant="outline" onClick={() => openRatingDialog(intent)} className="rounded-full">
                              <Star size={14} className="mr-1 text-[#F59E0B]" /> Calificar
                            </Button>
                            <Button size="sm" onClick={() => openRepublishDialog(intent.product_id, intent.product_title)} disabled={processing} className="bg-[#10B981] hover:bg-[#059669] rounded-full">
                              <RefreshCw size={14} className="mr-1" /> Republicar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Processed offers history */}
            {processedOffers.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-[#64748B] mb-3">Historial de ofertas</h3>
                <div className="space-y-2">
                  {processedOffers.slice(0, 5).map((offer) => (
                    <Card key={offer.offer_id} className="bg-[#F8FAFC] border-[#E2E8F0]">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <img
                              src={offer.product_image}
                              alt={offer.product_title}
                              className="w-10 h-10 object-cover rounded"
                            />
                            <div>
                              <p className="text-sm font-medium text-[#0F172A]">{offer.product_title}</p>
                              <p className="text-xs text-[#64748B] flex items-center gap-1.5 flex-wrap">
                                {offer.buyer_name} - ${offer.amount}
                                {offer.counter_amount && ` → $${offer.counter_amount}`}
                                {(offer.auto_accepted || offer.auto_countered) && (
                                  <span
                                    className="text-[9px] bg-amber-400 text-amber-900 font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide"
                                    title={offer.auto_accepted ? "Aceptada automáticamente" : "Contraofertada automáticamente"}
                                  >
                                    Auto
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <Badge className={
                            offer.status === "accepted" ? "bg-green-100 text-green-700" :
                            offer.status === "rejected" ? "bg-red-100 text-red-700" :
                            offer.status === "counter" ? "bg-orange-100 text-orange-700" :
                            offer.status === "expired" ? "bg-gray-200 text-gray-500" :
                            "bg-gray-100 text-gray-700"
                          }>
                            {offer.status === "accepted" ? "Aceptada" :
                             offer.status === "rejected" ? "Rechazada" :
                             offer.status === "counter" ? "Contraoferta" :
                             offer.status === "expired" ? "Expirada" : offer.status}
                          </Badge>
                          {offer.status === "accepted" && offer.buyer_phone && (
                            <a
                              href={`https://wa.me/${offer.buyer_phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hola ${offer.buyer_name}, tu oferta de $${offer.counter_amount || offer.amount} por "${offer.product_title}" fue aceptada. Coordinemos la entrega.`)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2"
                            >
                              <Button size="sm" className="bg-[#34D399] hover:bg-[#10B981] text-white h-7 px-2">
                                <MessageSquare size={14} />
                              </Button>
                            </a>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Active Bids Tab */}
          <TabsContent value="bids">
            {groupedBidsArray.length === 0 ? (
              <Card className="bg-white border-[#E2E8F0]">
                <CardContent className="py-12 text-center">
                  <TrendingUp className="h-12 w-12 text-[#CBD5E1] mx-auto mb-3" />
                  <p className="text-[#64748B]">No hay ofertas activas en tus productos</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {groupedBidsArray.map((group) => (
                  <AdminAuctionCard
                    key={group.productId}
                    productId={group.productId}
                    productTitle={group.productTitle}
                    productImage={group.productImage}
                    bids={group.bids}
                    endTime={group.endTime}
                  />
                ))}
              </div>
            )}

            {/* Auction-based Reserved Intents - Ganadores pendientes de pago */}
            {auctionIntentsReserved.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-[#10B981] mb-2 flex items-center gap-2">
                  <CheckCircle size={16} /> Subastas Ganadas - Reservados ({auctionIntentsReserved.length})
                </h3>
                <div className="space-y-3">
                  {auctionIntentsReserved.map((intent) => (
                    <Card key={intent.intent_id} className="bg-[#D1FAE5] border-[#10B981]">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <Link to={`/product/${intent.product_id}`}>
                            <img src={intent.product_image} alt={intent.product_title} className="w-16 h-16 object-cover rounded-lg" />
                          </Link>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-[#10B981] text-white text-xs">Reservado para {intent.buyer_name}</Badge>
                            </div>
                            <Link to={`/product/${intent.product_id}`} className="hover:text-[#475569]">
                              <h3 className="font-semibold text-[#0F172A] truncate">{intent.product_title}</h3>
                            </Link>
                            <p className="text-lg font-bold text-[#10B981]">${intent.product_price?.toLocaleString()}</p>
                            <p className="text-xs text-[#F59E0B] flex items-center gap-1">
                              <Clock size={12} />
                              Expira en {Math.max(0, 3 - Math.floor((Date.now() - new Date(intent.created_at).getTime()) / (1000 * 60 * 60 * 24)))} días
                            </p>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button size="sm" onClick={() => openMarkAsPaid(intent)} disabled={processing} className="bg-[#3B82F6] hover:bg-[#2563EB] rounded-full" data-testid={`mark-paid-auction-${intent.intent_id}`}>
                              <DollarSign size={14} className="mr-1" /> Marcar Pagado
                            </Button>
                            {intent.buyer_phone && (
                              <a
                                href={`https://wa.me/${intent.buyer_phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hola ${intent.buyer_name}, ¡felicidades por ganar la subasta de "${intent.product_title}" por $${intent.product_price}! Coordinemos la entrega.`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button size="sm" variant="outline" className="w-full border-[#34D399] text-[#34D399] hover:bg-[#D1FAE5] rounded-full">
                                  <MessageSquare size={14} className="mr-1" /> WhatsApp
                                </Button>
                              </a>
                            )}
                            <Button size="sm" variant="outline" onClick={() => handleRevokeIntent(intent.intent_id)} disabled={processing} className="border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2] rounded-full" data-testid={`revoke-auction-${intent.intent_id}`}>
                              <XCircle size={14} className="mr-1" /> Revocar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Auction-based Paid Intents - Pagados */}
            {auctionIntentsPaid.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-[#3B82F6] mb-2 flex items-center gap-2">
                  <DollarSign size={16} /> Subastas Pagadas ({auctionIntentsPaid.length})
                </h3>
                <div className="space-y-3">
                  {auctionIntentsPaid.map((intent) => (
                    <Card key={intent.intent_id} className="bg-[#EFF6FF] border-[#3B82F6]">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <img src={intent.product_image} alt={intent.product_title} className="w-16 h-16 object-cover rounded-lg" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-[#3B82F6] text-white text-xs">Subasta Pagada</Badge>
                            </div>
                            <h3 className="font-semibold text-[#0F172A] truncate">{intent.product_title}</h3>
                            <p className="text-sm text-[#64748B]">{intent.buyer_name}</p>
                            <p className="text-lg font-bold text-[#10B981]">${intent.product_price?.toLocaleString()}</p>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button size="sm" variant="outline" onClick={() => openRatingDialog(intent)} className="rounded-full">
                              <Star size={14} className="mr-1 text-[#F59E0B]" /> Calificar
                            </Button>
                            <Button size="sm" onClick={() => openRepublishDialog(intent.product_id, intent.product_title)} disabled={processing} className="bg-[#10B981] hover:bg-[#059669] rounded-full">
                              <RefreshCw size={14} className="mr-1" /> Republicar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Purchases Tab */}
          <TabsContent value="purchases">
            {data?.purchases?.length === 0 ? (
              <Card className="bg-white border-[#E2E8F0]">
                <CardContent className="py-12 text-center">
                  <ShoppingBag className="h-12 w-12 text-[#CBD5E1] mx-auto mb-3" />
                  <p className="text-[#64748B]">Aún no tienes ventas</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {data?.purchases?.map((purchase, index) => (
                  <Card key={purchase.bid_id || `purchase-${index}`} className="bg-white border-[#E2E8F0] hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <Link to={`/product/${purchase.product_id}`}>
                          <img
                            src={purchase.product_image}
                            alt={purchase.product_title}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        </Link>
                        
                        <div className="flex-1 min-w-0">
                          <Link to={`/product/${purchase.product_id}`} className="hover:text-[#475569]">
                            <h3 className="font-semibold text-[#0F172A] truncate">{purchase.product_title}</h3>
                          </Link>
                          
                          <div className="flex items-center gap-2 mt-1">
                            <Avatar className="h-5 w-5">
                              <AvatarImage src={purchase.buyer_picture} />
                              <AvatarFallback className="text-xs bg-[#475569] text-white">
                                {purchase.buyer_name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm text-[#64748B]">Comprado por {purchase.buyer_name}</span>
                          </div>
                          
                          <div className="flex items-center gap-2 mt-1 text-xs text-[#94A3B8]">
                            <Clock size={12} />
                            {formatDate(purchase.created_at)}
                          </div>
                        </div>
                        
                        <div className="text-right flex flex-col items-end gap-2">
                          <Badge className={purchase.is_paid ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                            {purchase.is_paid ? "Pagado" : "Pendiente"}
                          </Badge>
                          <p className="text-lg font-bold text-green-600">${purchase.amount}</p>
                          <span className="text-xs text-[#94A3B8]">
                            {purchase.sale_type === "auction" ? "Subasta" : "Venta directa"}
                          </span>
                          
                          {/* Confirm/Cancel buttons for pending sales */}
                          {!purchase.is_paid && (
                            <div className="flex gap-2 mt-2">
                              <Button
                                size="sm"
                                onClick={() => handleConfirmSale(purchase.product_id, true)}
                                disabled={processing}
                                className="bg-green-500 hover:bg-green-600 text-white text-xs"
                                data-testid={`confirm-sale-${purchase.product_id}`}
                              >
                                <Check size={14} className="mr-1" />
                                Confirmar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleConfirmSale(purchase.product_id, false)}
                                disabled={processing}
                                className="border-red-500 text-red-500 hover:bg-red-50 text-xs"
                                data-testid={`cancel-sale-${purchase.product_id}`}
                              >
                                <X size={14} className="mr-1" />
                                Cancelar
                              </Button>
                            </div>
                          )}
                          
                          {/* Delete button for sold/paid products */}
                          {purchase.is_paid && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteProduct(purchase.product_id)}
                              disabled={processing}
                              className="border-red-500 text-red-500 hover:bg-red-50 text-xs mt-2"
                              data-testid={`delete-product-${purchase.product_id}`}
                            >
                              <X size={14} className="mr-1" />
                              Eliminar
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Monthly Revenue Report Tab */}
          <TabsContent value="reports">
            <Card className="bg-white border-[#E2E8F0]">
              <CardContent className="p-4 sm:p-6 space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-bold text-[#0F172A] flex items-center gap-2">
                      <BarChart3 size={18} /> Reporte de ingresos por mes
                    </h3>
                    <p className="text-xs text-[#64748B] mt-0.5">Compara ventas generadas en la web vs. ventas manuales del inventario</p>
                  </div>
                  <Input
                    type="month"
                    value={reportMonth}
                    onChange={(e) => setReportMonth(e.target.value)}
                    max={currentMonth}
                    className="w-44 rounded-xl"
                    data-testid="report-month-input"
                  />
                </div>

                {/* Mini trend chart: last 6 months, web vs manual */}
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-3" data-testid="revenue-trend-chart">
                  <p className="text-xs font-semibold text-[#64748B] mb-1">Últimos 6 meses</p>
                  {trendLoading ? (
                    <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-[#64748B]" size={18} /></div>
                  ) : trendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={140}>
                      <BarChart data={trendData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barGap={3}>
                        <CartesianGrid vertical={false} stroke="#E2E8F0" />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                        <YAxis hide />
                        <Tooltip
                          formatter={(value, name) => [`$${value.toLocaleString()}`, name === "web_revenue" ? "Ventas Web" : "Inventario Manual"]}
                          labelFormatter={(label) => label}
                          contentStyle={{ borderRadius: 10, fontSize: 12, border: "1px solid #E2E8F0" }}
                        />
                        <Bar dataKey="web_revenue" name="web_revenue" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={22} />
                        <Bar dataKey="manual_revenue" name="manual_revenue" fill="#F97316" radius={[4, 4, 0, 0]} maxBarSize={22} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-xs text-[#94A3B8] text-center py-6">Sin datos suficientes todavía</p>
                  )}
                  <div className="flex items-center gap-4 mt-1 pl-1">
                    <span className="flex items-center gap-1.5 text-[11px] text-[#64748B]"><span className="w-2.5 h-2.5 rounded-full bg-[#3B82F6]" /> Ventas Web</span>
                    <span className="flex items-center gap-1.5 text-[11px] text-[#64748B]"><span className="w-2.5 h-2.5 rounded-full bg-[#F97316]" /> Inventario Manual</span>
                  </div>
                </div>

                {reportLoading ? (
                  <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#64748B]" /></div>
                ) : reportData ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="report-cards">
                      <div className="bg-[#EFF6FF] border border-blue-200 rounded-xl p-4">
                        <p className="text-xs text-blue-700 font-medium">Ventas Web</p>
                        <p className="text-2xl font-bold text-blue-700 mt-1" data-testid="report-web-revenue">${reportData.web_revenue.toLocaleString()}</p>
                        <p className="text-xs text-blue-500 mt-1">{reportData.web_sales_count} venta{reportData.web_sales_count === 1 ? "" : "s"}</p>
                      </div>
                      <div className="bg-[#FFF7ED] border border-orange-200 rounded-xl p-4">
                        <p className="text-xs text-orange-700 font-medium">Ventas Inventario Manual</p>
                        <p className="text-2xl font-bold text-orange-700 mt-1" data-testid="report-manual-revenue">${reportData.manual_revenue.toLocaleString()}</p>
                        <p className="text-xs text-orange-500 mt-1">{reportData.manual_sales_count} venta{reportData.manual_sales_count === 1 ? "" : "s"}</p>
                      </div>
                      <div className="bg-[#ECFDF5] border border-emerald-200 rounded-xl p-4">
                        <p className="text-xs text-emerald-700 font-medium">Total del mes</p>
                        <p className="text-2xl font-bold text-emerald-700 mt-1" data-testid="report-total-revenue">${reportData.total_revenue.toLocaleString()}</p>
                        <p className="text-xs text-emerald-500 mt-1">{reportData.web_sales_count + reportData.manual_sales_count} ventas en total</p>
                      </div>
                    </div>
                    {reportData.total_revenue === 0 && (
                      <p className="text-sm text-[#94A3B8] text-center py-4">No hay ventas registradas en este mes</p>
                    )}
                  </>
                ) : null}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Counter Offer Dialog */}
      <Dialog open={counterDialogOpen} onOpenChange={setCounterDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar Contraoferta</DialogTitle>
          </DialogHeader>
          {selectedOffer && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3">
                <img
                  src={selectedOffer.product_image}
                  alt={selectedOffer.product_title}
                  className="w-16 h-16 object-cover rounded-lg"
                />
                <div>
                  <p className="font-semibold">{selectedOffer.product_title}</p>
                  <p className="text-sm text-[#64748B]">Oferta de {selectedOffer.buyer_name}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-3 bg-[#F1F5F9] rounded-xl">
                  <p className="text-xs text-[#64748B]">Precio original</p>
                  <p className="text-xl font-bold text-[#0F172A]">${selectedOffer.product_price}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-xl">
                  <p className="text-xs text-orange-600">Su oferta</p>
                  <p className="text-xl font-bold text-orange-600">${selectedOffer.amount}</p>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-[#0F172A]">Tu contraoferta</label>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-2xl font-bold text-[#10B981]">$</span>
                  <Input
                    type="number"
                    value={counterAmount}
                    onChange={(e) => setCounterAmount(Number(e.target.value))}
                    className="text-2xl font-bold h-14"
                    data-testid="counter-amount-input"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={handleSendCounter}
              disabled={processing || counterAmount <= 0}
              className="w-full rounded-full h-12 bg-orange-500 hover:bg-orange-600"
              data-testid="send-counter-button"
            >
              {processing ? (
                <div className="spinner" style={{ width: 20, height: 20 }} />
              ) : (
                "Enviar Contraoferta"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog open={ratingDialogOpen} onOpenChange={setRatingDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Calificar a {selectedBuyer?.name}</DialogTitle>
            <DialogDescription>
              Califica la experiencia con este comprador
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className={`text-3xl transition-colors ${
                    star <= rating ? "text-[#F59E0B]" : "text-[#E2E8F0]"
                  }`}
                  data-testid={`star-${star}`}
                >
                  ★
                </button>
              ))}
            </div>
            <p className="text-center text-sm text-[#64748B]">
              {rating === 1 && "Muy malo"}
              {rating === 2 && "Malo"}
              {rating === 3 && "Regular"}
              {rating === 4 && "Bueno"}
              {rating === 5 && "Excelente"}
            </p>
            <Input
              placeholder="Comentario opcional..."
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
              className="rounded-xl"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRatingDialogOpen(false)} className="rounded-full">
              Cancelar
            </Button>
            <Button
              onClick={handleSubmitRating}
              disabled={processing}
              className="bg-[#F59E0B] hover:bg-[#D97706] rounded-full"
            >
              {processing ? <div className="spinner" style={{ width: 20, height: 20 }} /> : "Guardar Calificación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Paid + Cashback Dialog */}
      <Dialog open={paidDialogOpen} onOpenChange={setPaidDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="text-[#10B981]" size={20} />
              Marcar como Pagado
            </DialogTitle>
            <DialogDescription>
              "{paidIntent?.product_title}" — Precio: ${paidIntent?.product_price?.toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <p className="text-sm font-medium text-[#0F172A] mb-2">Cashback por review para el comprador</p>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setCashbackType("amount")}
                  className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-colors ${cashbackType === "amount" ? "bg-[#10B981] text-white border-[#10B981]" : "bg-white text-[#64748B] border-[#E2E8F0]"}`}
                  data-testid="cashback-type-amount"
                >
                  Monto ($)
                </button>
                <button
                  type="button"
                  onClick={() => setCashbackType("percent")}
                  className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-colors ${cashbackType === "percent" ? "bg-[#10B981] text-white border-[#10B981]" : "bg-white text-[#64748B] border-[#E2E8F0]"}`}
                  data-testid="cashback-type-percent"
                >
                  Porcentaje (%)
                </button>
              </div>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B] font-medium">
                  {cashbackType === "amount" ? "$" : "%"}
                </span>
                <Input
                  type="number"
                  min="0"
                  step={cashbackType === "amount" ? "0.25" : "0.5"}
                  value={cashbackValue}
                  onChange={(e) => setCashbackValue(e.target.value)}
                  placeholder={cashbackType === "amount" ? "Ej: 5" : "Ej: 2.5"}
                  className="pl-9 rounded-xl h-11"
                  data-testid="cashback-value-input"
                />
              </div>
              <p className="text-xs text-[#64748B] mt-2">
                Si lo dejas vacío, se aplicará el cashback mínimo de <strong>$2</strong>.
              </p>
            </div>
            <div className="p-3 bg-[#ECFDF5] border border-[#10B981]/30 rounded-xl flex items-center justify-between">
              <span className="text-sm text-[#065F46]">El comprador verá:</span>
              <span className="font-bold text-[#10B981]" data-testid="cashback-preview">${getComputedCashback()}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaidDialogOpen(false)} className="rounded-full">
              Cancelar
            </Button>
            <Button
              onClick={handleMarkAsPaid}
              disabled={processing}
              className="bg-[#3B82F6] hover:bg-[#2563EB] rounded-full"
              data-testid="confirm-mark-paid"
            >
              {processing ? <div className="spinner" style={{ width: 20, height: 20 }} /> : "Confirmar Pago"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Republish Dialog */}
      <Dialog open={republishDialogOpen} onOpenChange={setRepublishDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="text-[#10B981]" size={20} />
              Republicar Producto
            </DialogTitle>
            <DialogDescription>
              ¿Cómo deseas republicar "{republishProductTitle}"?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Button
              onClick={() => handleRepublishProduct(true)}
              disabled={processing}
              variant="outline"
              className="w-full h-16 justify-start gap-4 rounded-xl border-2 hover:border-[#10B981] hover:bg-[#F0FDF4]"
              data-testid="republish-edit-button"
            >
              <div className="p-2 bg-[#F0FDF4] rounded-lg">
                <RefreshCw size={24} className="text-[#10B981]" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-[#0F172A]">Editar y Republicar</p>
                <p className="text-xs text-[#64748B]">Modifica precio, descripción o imágenes</p>
              </div>
            </Button>
            
            <Button
              onClick={() => handleRepublishProduct(false)}
              disabled={processing}
              variant="outline"
              className="w-full h-16 justify-start gap-4 rounded-xl border-2 hover:border-[#3B82F6] hover:bg-[#EFF6FF]"
              data-testid="republish-direct-button"
            >
              <div className="p-2 bg-[#EFF6FF] rounded-lg">
                <CheckCircle size={24} className="text-[#3B82F6]" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-[#0F172A]">Republicar Directamente</p>
                <p className="text-xs text-[#64748B]">Mantener datos actuales del producto</p>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRepublishDialogOpen(false)}
              className="w-full rounded-full"
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear All Data Dialog */}
      <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={20} />
              Limpiar Todos los Datos
            </DialogTitle>
            <DialogDescription>
              Esta acción eliminará permanentemente:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
              <p className="text-sm text-red-700 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                Todas las ofertas y contraofertas
              </p>
              <p className="text-sm text-red-700 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                Todas las intenciones de compra
              </p>
              <p className="text-sm text-red-700 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                Todas las ofertas de subastas
              </p>
              <p className="text-sm text-red-700 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                Todas las notificaciones
              </p>
              <p className="text-sm text-red-700 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                Los productos se reiniciarán a estado "activo"
              </p>
            </div>
            <p className="text-xs text-center text-[#64748B]">
              Esta acción no se puede deshacer.
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setClearDialogOpen(false)}
              className="flex-1 rounded-full"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleClearAllData}
              disabled={processing}
              className="flex-1 rounded-full bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-clear-all-btn"
            >
              {processing ? (
                <div className="spinner" style={{ width: 20, height: 20 }} />
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Eliminar Todo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default ActiveOffersPage;
